import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, ChevronDown, ChevronUp, Eye, Pencil, Trash2, CheckCircle, Lock } from "lucide-react"
import { mlSheetService } from "../../services/mlSheetService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from "../common/index.js"

const formatMLSheetLabel = (id) => {
  if (!id) return "N/A"
  const raw = String(id).replace(/^ML-/i, "")
  return `ML-${raw}`
}

const getMLSheetRouteId = (id) => String(id || "").replace(/^ML-/i, "")

const getMLSheetStatusVariant = (status) => {
  const upper = (status || "").toUpperCase()
  if (upper === "OPEN") return "success"
  if (upper === "CLOSE") return "danger"
  return "default"
}

const MLSheetList = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [mlSheets, setMLSheets] = useState([])
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [filters, setFilters] = useState({
    search: "",
    from_location_ids: [],
    to_location_ids: [],
    from_date: "",
    to_date: "",
    status: "",
  })
  const [expandedAccessId, setExpandedAccessId] = useState(null)
  const [updatingStatusId, setUpdatingStatusId] = useState(null)

  useEffect(() => {
    fetchLocations()
  }, [])

  useEffect(() => {
    fetchMLSheets()
  }, [filters, currentPage])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expandedAccessId && !e.target.closest(".access-container")) {
        setExpandedAccessId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [expandedAccessId])

  const getLocationId = (loc) => String(loc.id || loc.hub_id)
  const getLocationLabel = (loc) => loc.hub_name || loc.location || loc.name

  const locationOptions = locations.map((loc) => ({
    value: getLocationId(loc),
    label: getLocationLabel(loc),
  }))

  const fetchLocations = async () => {
    try {
      const response = await mlSheetService.getLocations()
      const locationsData = response.data.hubs || response.data.data || response.data || []
      setLocations(Array.isArray(locationsData) ? locationsData : [])
    } catch (error) {
      console.error("Error fetching locations:", error)
    }
  }

  const fetchMLSheets = async () => {
    try {
      setLoading(true)
      const response = await mlSheetService.getAllMLSheets(currentPage, 20, filters)

      const sheetsData = response.data.data || response.data.ml_sheets || []
      setMLSheets(Array.isArray(sheetsData) ? sheetsData : [])
      setTotalPages(response.data.pagination?.pages || response.data.pagination?.total_pages || 1)
      setTotalRecords(response.data.pagination?.total || sheetsData.length)
    } catch (error) {
      console.error("Error fetching ML Sheets:", error)
      const status = error.response?.status
      if (status === 403) {
        Alert.error(error.response?.data?.error || "You do not have permission to view ML Sheets")
      } else {
        Alert.error(error.response?.data?.error || "Failed to load ML Sheets")
      }
      setMLSheets([])
    } finally {
      setLoading(false)
    }
  }

  const handleFilterPanelChange = (newValues) => {
    setFilters({ ...filters, ...newValues })
    setCurrentPage(1)
  }

  const handleLocationFilterChange = (name, values) => {
    setFilters({ ...filters, [name]: values })
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters({
      search: "",
      from_location_ids: [],
      to_location_ids: [],
      from_date: "",
      to_date: "",
      status: "",
    })
    setCurrentPage(1)
  }

  const getAccessUserIds = (sheet) =>
    (sheet.access_users || []).map((u) => (typeof u === "object" ? u.user_id || u.id : u))

  const canChangeMLSheetStatus = (sheet) => {
    if (!user) return false
    if (["System Admin", "Admin", "Accountant"].includes(user.role)) return true
    if (user.role === "Agent") {
      return getAccessUserIds(sheet).includes(user.user_id)
    }
    return false
  }

  const handleMLSheetStatusChange = async (sheet, newStatus) => {
    const currentStatus = (sheet.status || "").toUpperCase()
    if (newStatus === currentStatus) return

    if (!window.confirm(`Change ML Sheet ${formatMLSheetLabel(sheet.ml_sheet_id)} status to ${newStatus}?`)) return

    try {
      setUpdatingStatusId(sheet.ml_sheet_id)
      await mlSheetService.updateMLSheetStatus(getMLSheetRouteId(sheet.ml_sheet_id), newStatus)
      Alert.success("ML Sheet status updated successfully")
      fetchMLSheets()
    } catch (error) {
      console.error("Error updating ML Sheet status:", error)
      const status = error.response?.status
      const msg = error.response?.data?.error
      if (status === 403) {
        Alert.error(msg || "You do not have permission to update this ML Sheet status")
      } else if (status === 404) {
        Alert.error(msg || "ML Sheet not found")
      } else {
        Alert.error(msg || "Failed to update ML Sheet status")
      }
    } finally {
      setUpdatingStatusId(null)
    }
  }

  const handleDelete = async (mlSheetId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this ML Sheet? All shipments will be reverted to booked status."
      )
    ) {
      return
    }

    try {
      await mlSheetService.deleteMLSheet(getMLSheetRouteId(mlSheetId))
      Alert.success("ML Sheet deleted successfully")
      fetchMLSheets()
    } catch (error) {
      console.error("Error deleting ML Sheet:", error)
      Alert.error(error.response?.data?.error || "Failed to delete ML Sheet")
    }
  }

  const openDetail = (mlSheetId) => {
    navigate(`/ml-sheets/view/${getMLSheetRouteId(mlSheetId)}`)
  }

  const canEdit = () => {
    return user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant"
  }

  const canDelete = () => {
    return user?.role === "System Admin" || user?.role === "Admin"
  }

  const renderRoute = (sheet) => (
    <div className="flex items-center justify-center gap-2 font-semiboldregular text-secondary">
      <span>
        {sheet.from_locations && sheet.from_locations.length > 0
          ? sheet.from_locations
              .map((loc) => loc.location || loc.hub_name || loc.name)
              .join(", ")
          : sheet.from_location?.location || sheet.from_location || "N/A"}
      </span>
      <span className="text-secondary">→</span>
      <span>
        {sheet.to_locations && sheet.to_locations.length > 0
          ? sheet.to_locations
              .map((loc) => loc.location || loc.hub_name || loc.name)
              .join(", ")
          : sheet.to_location?.location || sheet.to_location || "N/A"}
      </span>
    </div>
  )

  const columns = [
    {
      key: "sr_no",
      label: "Sr No",
      width: "6%",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (_, index) => (currentPage - 1) * 20 + index + 1,
    },
    {
      key: "ml_sheet_id",
      label: "ML Sheet #",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (sheet) => (
        <span className="text-secondary font-regular text-sm">
          {formatMLSheetLabel(sheet.ml_sheet_id)}
        </span>
      ),
    },
    {
      key: "route",
      label: "From → To",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (sheet) => renderRoute(sheet),
    },
    {
      key: "vehicle_number",
      label: "Vehicle",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (sheet) => (
        <div className="font-regular text-secondary text-sm">{sheet.vehicle_number}</div>
      ),
    },
    {
      key: "shipments",
      label: "Shipments",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (sheet) => (
        <span className="inline-block px-3 py-1 text-secondary font-regular text-sm">
          {sheet.shipment_ids?.length || 0}
        </span>
      ),
    },
    {
      key: "access_users",
      label: "Access Users",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (sheet) => (
        <div
          className="relative access-container"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() =>
              setExpandedAccessId(
                expandedAccessId === sheet.ml_sheet_id ? null : sheet.ml_sheet_id
              )
            }
            className="flex items-center gap-1 text-secondary font-regular text-sm mx-auto"
          >
            {expandedAccessId === sheet.ml_sheet_id ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
            {sheet.access_users?.length || 0} Users
          </button>
          {expandedAccessId === sheet.ml_sheet_id && (
            <div className="absolute top-full mt-1 left-0 bg-white border border-blue-300 rounded-sm shadow-lg z-10 min-w-48">
              {sheet.access_users?.map((accessUser) => {
                const userId = accessUser.user_id || accessUser
                const userName = accessUser.name || `User ${userId}`
                const userLocation = accessUser.location || ""
                return (
                  <button
                    key={userId}
                    type="button"
                    onClick={() => navigate(`/users/view/${userId}`)}
                    className="block w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-200 font-semibold text-gray-800"
                  >
                    <div className="font-semibold">
                      {userName} - {userLocation}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      cellClassName: "text-center",
      headerClassName: "text-center",
      render: (sheet) => (
        <StatusBadge
          status={sheet.status}
          variant={getMLSheetStatusVariant(sheet.status)}
          label={(sheet.status || "").toUpperCase()}
        />
      ),
    },
    {
      key: "created_at",
      label: "Created",
      cellClassName: "text-center text-gray-600",
      headerClassName: "text-center",
      render: (sheet) => new Date(sheet.created_at).toLocaleDateString("en-IN"),
    },
  ]

  const getActions = (sheet) => [
    {
      label: "View",
      icon: Eye,
      onClick: () => openDetail(sheet.ml_sheet_id),
    },
    {
      label: "Edit",
      icon: Pencil,
      onClick: () => navigate(`/ml-sheets/edit/${getMLSheetRouteId(sheet.ml_sheet_id)}`),
      visible: canEdit(),
    },
    {
      label: "Mark as Open",
      icon: CheckCircle,
      variant: "success",
      onClick: () => {
        if (updatingStatusId !== sheet.ml_sheet_id) handleMLSheetStatusChange(sheet, "OPEN")
      },
      visible:
        canChangeMLSheetStatus(sheet) && (sheet.status || "").toUpperCase() !== "OPEN",
    },
    {
      label: "Mark as Close",
      icon: Lock,
      variant: "warning",
      onClick: () => {
        if (updatingStatusId !== sheet.ml_sheet_id) handleMLSheetStatusChange(sheet, "CLOSE")
      },
      visible:
        canChangeMLSheetStatus(sheet) && (sheet.status || "").toUpperCase() !== "CLOSE",
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      onClick: () => handleDelete(sheet.ml_sheet_id),
      visible: canDelete(),
    },
  ]

  const mobileCardRender = (sheet) => (
    <div>
      <div className="mb-3">
        <div className="flex justify-between items-start gap-2">
          <div className="text-blue-600 font-bold text-lg">
            {formatMLSheetLabel(sheet.ml_sheet_id)}
          </div>
          <StatusBadge
            status={sheet.status}
            variant={getMLSheetStatusVariant(sheet.status)}
            label={(sheet.status || "").toUpperCase()}
          />
        </div>
        <div className="text-sm text-gray-600 mt-1">{renderRoute(sheet)}</div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <div>
          <span className="text-gray-600">Vehicle:</span>
          <div className="font-semibold">{sheet.vehicle_number}</div>
        </div>
        <div>
          <span className="text-gray-600">Shipments:</span>
          <div className="font-semibold">{sheet.shipment_ids?.length || 0}</div>
        </div>
      </div>
      <div
        className="flex justify-between items-center pt-2 border-t border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs text-gray-500">
          {new Date(sheet.created_at).toLocaleDateString("en-IN")}
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => openDetail(sheet.ml_sheet_id)}
            className="text-brand font-semibold text-sm"
          >
            View
          </button>
          {canEdit() && (
            <button
              type="button"
              onClick={() =>
                navigate(`/ml-sheets/edit/${getMLSheetRouteId(sheet.ml_sheet_id)}`)
              }
              className="text-blue-600 font-semibold text-sm"
            >
              Edit
            </button>
          )}
          {canChangeMLSheetStatus(sheet) && (sheet.status || "").toUpperCase() !== "OPEN" && (
            <button
              type="button"
              onClick={() => handleMLSheetStatusChange(sheet, "OPEN")}
              disabled={updatingStatusId === sheet.ml_sheet_id}
              className="text-green-600 font-semibold text-sm disabled:opacity-50"
            >
              Open
            </button>
          )}
          {canChangeMLSheetStatus(sheet) && (sheet.status || "").toUpperCase() !== "CLOSE" && (
            <button
              type="button"
              onClick={() => handleMLSheetStatusChange(sheet, "CLOSE")}
              disabled={updatingStatusId === sheet.ml_sheet_id}
              className="text-yellow-700 font-semibold text-sm disabled:opacity-50"
            >
              Close
            </button>
          )}
          {canDelete() && (
            <button
              type="button"
              onClick={() => handleDelete(sheet.ml_sheet_id)}
              className="text-red-600 font-semibold text-sm"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )

  const canCreate =
    user?.role === "System Admin" ||
    user?.role === "Admin" ||
    user?.role === "Accountant" ||
    user?.role === "Agent"

  return (
    <PageLayout
      title="Master Loading Sheets"
      actions={
        canCreate ? (
          <Button icon={Plus} onClick={() => navigate("/ml-sheets/add")}>
            Create ML
          </Button>
        ) : null
      }
    >
      <FilterPanel
        filters={[
          { key: "search", label: "ML Sheet ID", placeholder: "e.g. VA000010" },
          {
            key: "status",
            label: "Status",
            type: "select",
            options: [
              { value: "OPEN", label: "Open" },
              { value: "CLOSE", label: "Close" },
            ],
          },
          { key: "from_date", label: "From Date", type: "date" },
          { key: "to_date", label: "To Date", type: "date" },
          {
            key: "from_location_ids",
            label: "Sender Location",
            type: "multiselect",
            placeholder: "All sender locations",
            options: locationOptions,
          },
          {
            key: "to_location_ids",
            label: "Receiver Location",
            type: "multiselect",
            placeholder: "All receiver locations",
            options: locationOptions,
          },
        ]}
        values={filters}
        onChange={handleFilterPanelChange}
        onClear={handleClearFilters}
      />

      <DataTable
        columns={columns}
        data={mlSheets}
        loading={loading}
        emptyMessage="No ML Sheets found"
        rowKey="ml_sheet_id"
        onRowClick={(sheet) => openDetail(sheet.ml_sheet_id)}
        actions={getActions}
        mobileCardRender={mobileCardRender}
      />

      {!loading && mlSheets.length > 0 && (
        <div className="text-sm text-gray-600 text-center mt-2">
          Showing {(currentPage - 1) * 20 + 1} to {Math.min(currentPage * 20, totalRecords)} of{" "}
          {totalRecords} entries
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  )
}

export default MLSheetList
